import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useTenant } from "@/context/TenantContext";
import { toast } from "sonner";
import { ArrowLeft, LogOut, KeyRound, Send, Mail, Monitor, Clock, Search, CheckCircle2, AlertTriangle, PauseCircle, Copy, RotateCcw, Link2, Home, ExternalLink } from "lucide-react";
import TenantBrand from "@/components/TenantBrand";
import { parseDelivered } from "@/lib/deliveredFormat";
import HalloweenDecor from "@/components/HalloweenDecor";

const STATUS_STYLE = {
  resuelto: { border: "border-l-emerald-500", badge: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: CheckCircle2 },
  pendiente: { border: "border-l-amber-500", badge: "bg-amber-50 text-amber-700 border-amber-200", icon: Clock },
};
const REPLACEMENT_PREFIXES = ["Reemplazo: ", "Premio Ruleta: "];
// Qué se puede pedir además del código (pedido del dueño, 2026-10-06) --
// mismas reglas que _tipo_permitido en el backend.
const TIPOS = {
  codigo: { label: "Código", icon: KeyRound, resultado: "Tu código", boton: "Pedir código" },
  link: { label: "Link para cambiar contraseña", icon: Link2, resultado: "Tu link para restablecer la contraseña", boton: "Pedir link" },
  hogar: { label: "Código de hogar", icon: Home, resultado: "Tu código de hogar", boton: "Pedir código de hogar" },
};
// Solo las plataformas que de verdad piden código de acceso -- el resto de
// lo que el cliente haya comprado (Canva, Office, IPTV, Crunchyroll, etc.)
// no aplica aquí y solo confundía en el desplegable.
const PLATAFORMAS_CON_CODIGO = ["netflix", "disney", "fox one", "apple tv", "universal"];
// HBO Max completa: solo link para cambiar contraseña (pedido del dueño,
// 2026-10-10) -- mismo criterio que alesita_max.es_max_completa.
const esMaxCompleta = (nombre) => {
  const n = (nombre || "").toLowerCase();
  return (n.includes("hbo") || /\bmax\b/.test(n)) && n.includes("completa");
};
const esPlataformaConCodigo = (nombre) => {
  const n = (nombre || "").toLowerCase();
  return PLATAFORMAS_CON_CODIGO.some((p) => n.includes(p)) || esMaxCompleta(n);
};

export default function Codigos() {
  const { user, logout } = useAuth();
  const esAdmin = user?.role === "admin";
  const { tenant } = useTenant();
  const navigate = useNavigate();
  const [purchases, setPurchases] = useState([]);
  const [catalogo, setCatalogo] = useState([]);
  const [requests, setRequests] = useState([]);
  const [busy, setBusy] = useState(false);
  const [plataforma, setPlataforma] = useState("");
  const [correo, setCorreo] = useState("");
  const [compraSel, setCompraSel] = useState(null);
  const [digitos, setDigitos] = useState(null);
  const [tipo, setTipo] = useState("codigo");

  const load = () => api.get("/my/codes").then((r) => setRequests(r.data)).catch(() => {});
  const loadPurchases = () => api.get("/my/purchases").then((r) => setPurchases(r.data)).catch(() => {});
  useEffect(() => { load(); loadPurchases(); }, []);
  // El admin revisa códigos de cuentas de todos lados, no solo de lo que él
  // compró -- a él se le listan TODAS las plataformas con código del catálogo.
  useEffect(() => {
    if (esAdmin) api.get("/products").then((r) => setCatalogo(r.data.map((p) => p.name).filter(esPlataformaConCodigo))).catch(() => {});
  }, [esAdmin]);

  // Mientras haya una solicitud pendiente, revisa cada 5s -- así el código
  // aparece solo en cuanto se resuelve, sin que el cliente tenga que
  // refrescar la página a mano. Se detiene solo en cuanto ya no hay
  // pendientes, para no seguir pegándole al backend sin necesidad.
  const hayPendientes = useMemo(() => requests.some((r) => r.status === "pendiente"), [requests]);
  useEffect(() => {
    if (!hayPendientes) return;
    const interval = setInterval(load, 5000);
    return () => clearInterval(interval);
  }, [hayPendientes]);

  // Mismas cuentas realmente compradas por el cliente que ya se usan en
  // Reportes -- así elige de una lista real en vez de escribir el correo a
  // mano, sin duplicar el flujo de "elegir plataforma primero".
  const misCompras = useMemo(() => {
    const list = [];
    purchases.forEach((s) => {
      (s.items || []).forEach((it) => {
        const matchedPrefix = REPLACEMENT_PREFIXES.find((pre) => it.product_name?.startsWith(pre));
        const productName = matchedPrefix ? it.product_name.slice(matchedPrefix.length) : it.product_name;
        (it.delivered && it.delivered.length ? it.delivered : [""]).forEach((d, di) => {
          const p = d ? parseDelivered(d) : {};
          list.push({
            key: `${s.id}-${it.product_id}-${di}`, sale_id: s.id, product_name: productName,
            correo: p.texto || p.correo || d, fecha: s.created_at,
          });
        });
      });
    });
    return list.filter((c) => esPlataformaConCodigo(c.product_name)).sort((a, b) => new Date(b.fecha) - new Date(a.fecha));
  }, [purchases]);

  const misPlataformas = useMemo(() => {
    const seen = new Set();
    const list = [];
    misCompras.forEach((c) => { if (!seen.has(c.product_name)) { seen.add(c.product_name); list.push(c.product_name); } });
    catalogo.forEach((name) => { if (!seen.has(name)) { seen.add(name); list.push(name); } });
    return list;
  }, [misCompras, catalogo]);

  // Un mismo correo sale UNA vez aunque tenga varios perfiles comprados --
  // para pedir el código solo importa el correo. misCompras viene ordenado
  // de la más reciente a la más vieja, así que se queda la compra más nueva.
  const cuentasDePlataforma = useMemo(() => {
    const seen = new Set();
    return misCompras.filter((c) => {
      if (c.product_name !== plataforma) return false;
      const k = (c.correo || "").trim().toLowerCase();
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    });
  }, [misCompras, plataforma]);

  const onPlataformaSelect = (e) => {
    const val = e.target.value;
    setPlataforma(val);
    setCompraSel(null);
    setCorreo("");
  };
  const pickCompra = (c) => { setCompraSel(c); setCorreo(c.correo); };

  // Si el correo que escribe/pega coincide exacto con una de sus cuentas
  // reales, se detecta sola la plataforma -- no hace falta elegirla primero
  // a mano. No se limpia la plataforma si no hay match (podría ser que la
  // eligió manual y sigue escribiendo el correo).
  const onCorreoChange = (e) => {
    const val = e.target.value;
    setCorreo(val);
    const needle = val.trim().toLowerCase();
    const match = needle ? misCompras.find((c) => c.correo.toLowerCase() === needle) : null;
    if (match) {
      setCompraSel(match);
      setPlataforma(match.product_name);
    } else {
      setCompraSel(null);
    }
  };

  const pausado = !!tenant?.codigos_pausados;

  // Netflix Perfil/Completa con gmail o @galisolstrem.com (pool de JR
  // Streaming) tiene DOS códigos distintos: el de inicio de sesión (4
  // dígitos) y el de acceso (6 dígitos), cada uno sale por un botón distinto
  // del bot -- hay que saber cuál pide. Antes solo preguntaba en Completa.
  const plat = (plataforma || "").toLowerCase();
  const correoLower = correo.trim().toLowerCase();
  const esNetflix = plat.includes("netflix");
  const esPriv = plat.includes("priv");
  // Link: Netflix completa o privada. Hogar: Netflix perfil o completa (las
  // privadas no piden código de hogar). "priv" gana sobre "perfil".
  const maxCompleta = esMaxCompleta(plat);
  const tiposDisponibles = maxCompleta ? ["link"] : ["codigo",
    ...(esNetflix && (esPriv || plat.includes("completa")) ? ["link"] : []),
    ...(esNetflix && !esPriv && (plat.includes("perfil") || plat.includes("completa")) ? ["hogar"] : [])];
  const tipoActual = tiposDisponibles.includes(tipo) ? tipo : tiposDisponibles[0];
  // Privadas también eligen 4 o 6 dígitos (iker tiene un botón para cada uno).
  const preguntaDigitos = tipoActual === "codigo" && esNetflix && (esPriv
    || ((plat.includes("perfil") || plat.includes("completa"))
      && (correoLower.endsWith("@gmail.com") || correoLower.endsWith("@galisolstrem.com"))));

  const copyCode = (r) => {
    navigator.clipboard?.writeText(r.code).then(() => {
      toast.success(r.code.startsWith("http") ? "Link copiado" : "Código copiado");
      // A los 30s se borra de verdad (no solo se oculta) -- así la lista solo
      // muestra el código más reciente sin viejos ya usados por ahí confundiendo.
      setTimeout(() => {
        api.delete(`/codes/${r.id}`).catch(() => {});
        setRequests((rs) => rs.filter((x) => x.id !== r.id));
      }, 30000);
    });
  };

  const retryRequest = async (r) => {
    try {
      await api.delete(`/codes/${r.id}`).catch(() => {});
      await api.post("/codes/request", { correo_cuenta: r.correo_cuenta, plataforma: r.plataforma, sale_id: null, digitos: r.digitos || null, tipo: r.tipo || "codigo" });
      toast.success("Solicitud reenviada -- te avisamos en cuanto tengamos tu código");
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const submit = async () => {
    if (!correo.trim()) return toast.error("Escribe o elige el correo de la cuenta");
    if (preguntaDigitos && !digitos) return toast.error("Elige si el código que te pide Netflix es de 4 o de 6 dígitos");
    setBusy(true);
    try {
      await api.post("/codes/request", { correo_cuenta: correo.trim(), plataforma, sale_id: compraSel?.sale_id || null, digitos: preguntaDigitos ? digitos : null, tipo: tipoActual });
      toast.success(`Solicitud enviada -- te avisamos en cuanto tengamos tu ${tipoActual === "link" ? "link" : "código"}`);
      setCorreo(""); setPlataforma(""); setCompraSel(null); setDigitos(null); setTipo("codigo");
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-40 bg-primary text-white shadow-md">
        <div className="max-w-3xl mx-auto px-4 h-[70px] flex items-center justify-between">
          <div className="flex items-center gap-3"><TenantBrand tenant={tenant} imgClassName="h-10 w-auto object-contain" textClassName="font-head text-lg font-extrabold uppercase tracking-tight text-current" /><span className="font-head text-lg font-bold">Pedir Código</span></div>
          <div className="flex items-center gap-2">
            <button data-testid="codigos-back" onClick={() => navigate("/compras")} className="w-10 h-10 grid place-items-center rounded-lg bg-white/15 hover:bg-white/25 transition-colors"><ArrowLeft size={18} /></button>
            <button data-testid="codigos-logout" onClick={() => { logout(); navigate("/login"); }} className="w-10 h-10 grid place-items-center rounded-lg bg-white/15 hover:bg-white/25 transition-colors"><LogOut size={18} /></button>
          </div>
        </div>
        <HalloweenDecor />
      </header>

      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="bg-white rounded-2xl border border-slate-200 p-6 mb-6">
          <div className="flex items-center gap-2 pb-4 mb-5 border-b border-slate-100"><KeyRound size={22} className="text-primary" /><h3 className="font-head text-xl font-bold text-slate-900">Pedir código de acceso</h3></div>
          <p className="text-sm text-slate-500 mb-5">Para Netflix, Disney+, Fox One, Apple TV y otras cuentas que piden un código de verificación al iniciar sesión: pide aquí el código y te avisamos apenas lo tengamos.</p>

          {pausado ? (
            <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3.5 text-amber-800">
              <PauseCircle size={18} className="shrink-0 mt-0.5" />
              <p className="text-sm font-semibold">{tenant.codigos_pausados_mensaje || "La sección de códigos está pausada por ahora -- vuelve a intentarlo más tarde."}</p>
            </div>
          ) : (
            <div className="space-y-5">
              <RField icon={Monitor} label="Plataforma (opcional)">
                <select data-testid="cod-plataforma" value={plataforma} onChange={onPlataformaSelect} className={ic()}>
                  <option value="">Selecciona el servicio...</option>
                  {misPlataformas.map((name) => <option key={name} value={name}>{name}</option>)}
                </select>
              </RField>

              {plataforma && cuentasDePlataforma.length > 0 && (
                <div className="space-y-1.5">
                  {cuentasDePlataforma.map((c) => (
                    <button type="button" key={c.key} onClick={() => pickCompra(c)}
                      className={`w-full text-left px-4 py-2.5 rounded-lg border text-sm transition-colors ${compraSel?.key === c.key ? "border-primary bg-primary/5 text-primary font-semibold" : "border-slate-200 hover:bg-slate-50 text-slate-700"}`}>
                      {c.correo}
                    </button>
                  ))}
                </div>
              )}

              <RField icon={Mail} label="Correo de la cuenta">
                <div className="relative">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input data-testid="cod-correo" type="email" value={correo} onChange={onCorreoChange}
                    placeholder="Pega o escribe el correo de la cuenta..." className={`pl-10 ${ic()}`} />
                </div>
                {compraSel && (
                  <div className="mt-2 flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 text-sm">
                    <CheckCircle2 size={16} className="text-emerald-600 shrink-0" />
                    <span className="text-emerald-800">Plataforma detectada: <b>{compraSel.product_name}</b></span>
                  </div>
                )}
              </RField>

              {tiposDisponibles.length > 1 && (
                <RField icon={KeyRound} label="¿Qué necesitas?">
                  <div className={`grid gap-2 ${tiposDisponibles.length === 3 ? "grid-cols-1 sm:grid-cols-3" : "grid-cols-2"}`}>
                    {tiposDisponibles.map((t) => {
                      const T = TIPOS[t];
                      return (
                        <button type="button" key={t} data-testid={`cod-tipo-${t}`} onClick={() => setTipo(t)}
                          className={`flex items-center justify-center gap-1.5 px-3 py-3 rounded-lg border text-sm font-bold transition-colors ${tipoActual === t ? "border-primary bg-primary/5 text-primary" : "border-slate-200 hover:bg-slate-50 text-slate-700"}`}>
                          <T.icon size={15} /> {T.label}
                        </button>
                      );
                    })}
                  </div>
                </RField>
              )}

              {maxCompleta && (
                <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3.5 text-amber-800">
                  <AlertTriangle size={18} className="shrink-0 mt-0.5" />
                  <p className="text-sm">Primero, en la app o página de Max toca <b>"¿Olvidaste tu contraseña?"</b> y escribe este correo. Después pide aquí el link para cambiarla.</p>
                </div>
              )}

              {preguntaDigitos && (
                <RField icon={KeyRound} label="¿De cuántos dígitos es el código?">
                  <div className="grid grid-cols-2 gap-2">
                    {[4, 6].map((n) => (
                      <button type="button" key={n} data-testid={`cod-digitos-${n}`} onClick={() => setDigitos(n)}
                        className={`px-4 py-3 rounded-lg border text-sm font-bold transition-colors ${digitos === n ? "border-primary bg-primary/5 text-primary" : "border-slate-200 hover:bg-slate-50 text-slate-700"}`}>
                        {n} dígitos
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-slate-500 mt-1.5">Revisa cuántos dígitos te pide la pantalla de Netflix.</p>
                </RField>
              )}

              <button data-testid="cod-submit" disabled={busy} onClick={submit} className="w-full flex items-center justify-center gap-2 bg-primary hover:bg-primary-hover disabled:opacity-60 text-white font-bold rounded-lg py-3.5 transition-colors"><Send size={18} /> {TIPOS[tipoActual].boton}</button>
            </div>
          )}
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <div className="flex items-center gap-2 pb-4 mb-4 border-b border-slate-100"><KeyRound size={22} className="text-primary" /><h3 className="font-head text-xl font-bold text-slate-900">Mis solicitudes</h3></div>
          {requests.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-14 h-14 rounded-2xl bg-slate-100 grid place-items-center mx-auto text-slate-400"><KeyRound size={26} /></div>
              <p className="font-head font-bold text-slate-700 mt-3">Sin solicitudes</p>
              <p className="text-sm text-slate-400 mt-1">Cuando pidas un código, aparecerá aquí.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {requests.map((r) => {
                const style = STATUS_STYLE[r.status] || STATUS_STYLE.pendiente;
                const StatusIcon = style.icon;
                return (
                  <div key={r.id} data-testid={`mycode-${r.id}`} className={`border border-slate-200 border-l-4 rounded-xl p-4 ${style.border}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-2.5 min-w-0">
                        <span className="w-9 h-9 shrink-0 rounded-lg bg-primary/10 grid place-items-center text-primary"><Monitor size={16} /></span>
                        <div className="min-w-0">
                          <p className="font-bold text-slate-900 truncate">{r.plataforma || "Cuenta"}{r.tipo && r.tipo !== "codigo" && <span className="ml-1.5 text-xs font-semibold text-slate-500">· {TIPOS[r.tipo]?.label}</span>}</p>
                          <p className="text-xs text-slate-500 truncate">{r.correo_cuenta}</p>
                        </div>
                      </div>
                      <span className={`shrink-0 flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-full border uppercase tracking-wide ${style.badge}`}>
                        <StatusIcon size={12} /> {r.status}
                      </span>
                    </div>
                    {r.status === "resuelto" && r.code && (
                      <div className="mt-3 bg-slate-900 rounded-lg p-3 flex items-center justify-between gap-3">
                        <div className="text-center flex-1 min-w-0">
                          <p className="text-xs font-bold uppercase tracking-wide text-emerald-400 mb-1">{(TIPOS[r.tipo] || TIPOS.codigo).resultado}</p>
                          {r.code.startsWith("http") ? (
                            <a href={r.code} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm font-bold text-white underline break-all">
                              <ExternalLink size={14} className="shrink-0" /> Abrir link
                            </a>
                          ) : (
                            <p className="font-head text-2xl font-extrabold text-white tracking-widest">{r.code}</p>
                          )}
                        </div>
                        <button type="button" onClick={() => copyCode(r)} aria-label={r.code.startsWith("http") ? "Copiar link" : "Copiar código"}
                          className="shrink-0 w-9 h-9 grid place-items-center rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white transition-colors">
                          <Copy size={16} />
                        </button>
                      </div>
                    )}
                    {r.status === "pendiente" && (
                      <div className="mt-3">
                        <button type="button" onClick={() => retryRequest(r)}
                          className="flex items-center gap-1.5 border border-slate-200 hover:border-primary hover:text-primary text-slate-600 text-xs font-bold rounded-md px-3 py-2 transition-colors">
                          <RotateCcw size={12} /> Reintentar
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ic() { return "w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-red-500 focus:bg-white transition-colors"; }
function RField({ icon: Icon, label, children }) {
  return <label className="block"><span className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-slate-600 mb-1.5"><Icon size={13} className="text-primary" /> {label}</span>{children}</label>;
}
